import { Injectable } from '@angular/core';
import * as signalR from '@aspnet/signalr';
import { HttpClient } from '@angular/common/http';

export interface CdnSessionRegistrationResult {
  accepted: boolean;
  environment: string;
  userId: string;
  connectionId?: string;
  reason?: string;
}



@Injectable({
  providedIn: 'root'
})
export class AuthenticateService {
public signalrUrl = "";
public allowSignalR = true;
private configLoaded: Promise<void>;
private environment = "";
private accessToken = "";
private reconnectTimeout: any = null;
private disconnectRequested = false;
private reconnecting = false;
private reconnectPromise: Promise<boolean> | null = null;
private lastSessionRejection: CdnSessionRegistrationResult | null = null;

  constructor(private http: HttpClient){
    this.configLoaded = this.http.get('/assets/config.json').toPromise().then((response: any) => {
      this.signalrUrl = response.signalrUrl;
      this.allowSignalR = response.allowSignalR !== false;
    });
  }

  private connection: signalR.HubConnection | null = null;

  registerSession(environment: string, accessToken: string): Promise<CdnSessionRegistrationResult> {
    return this.configLoaded.then(() => {
      if (!this.allowSignalR) {
        return {
          accepted: true,
          environment,
          userId: ""
        };
      }

      this.environment = environment;
      this.accessToken = accessToken;
      this.disconnectRequested = false;
      this.lastSessionRejection = null;

      if (this.reconnectTimeout) {
        clearTimeout(this.reconnectTimeout);
        this.reconnectTimeout = null;
      }

      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(this.signalrUrl, {
          accessTokenFactory: () => this.accessToken
        })
        .build();

      this.connection.on('LoginRejected', (result: CdnSessionRegistrationResult) => {
        this.lastSessionRejection = result;
        //console.warn('Login rejected', result.reason);
      });

      this.connection.on('LoginAccepted', (result: CdnSessionRegistrationResult) => {
        //console.log('Login accepted', result);
      });

      this.connection.onclose(() => {
        //console.warn('SignalR disconnected');
        this.scheduleReconnect();
      });

      return this.connection
      .start()
      .then(() => {
        return this.connection!.invoke<CdnSessionRegistrationResult>('RegisterSession', {
          environment
        });
      });
    });
  }

  public refreshSession(environment: string): Promise<boolean> {
    if (!this.allowSignalR) {
      return Promise.resolve(true);
    }

    this.lastSessionRejection = null;

    return this.ensureConnected().then(connected => {
      if (!connected || !this.connection) {
        throw new Error("SignalR connection is not connected.");
      }

      return this.connection.invoke<boolean>('RefreshSession', {
        environment
      });
    });
  }

  public getLastSessionRejection(): CdnSessionRegistrationResult | null {
    return this.lastSessionRejection;
  }

  public disconnect(): Promise<void> {
    this.disconnectRequested = true;

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (!this.connection) {
      return Promise.resolve();
    }

    return this.connection.stop().then(() => {
      this.connection = null;
    });
  }

  private scheduleReconnect() {
    if (this.disconnectRequested || !this.connection || this.reconnectTimeout) {
      return;
    }

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = null;
      this.reconnect();
    }, 5000);
  }

  private reconnect() {
    if (this.disconnectRequested || !this.connection || this.reconnecting) {
      return;
    }

    this.ensureConnected()
      .then(connected => {
        if (!connected || !this.connection) {
          return false;
        }

        this.lastSessionRejection = null;

        return this.connection!.invoke<boolean>('RefreshSession', {
          environment: this.environment
        });
      })
      .then(refreshed => {
        if (!refreshed) {
          this.scheduleReconnect();
        }
      })
      .catch(error => {
        console.error('SignalR reconnect failed', error);
        this.scheduleReconnect();
      });
  }

  private ensureConnected(): Promise<boolean> {
    if (this.disconnectRequested || !this.connection) {
      return Promise.resolve(false);
    }

    if (this.connection.state === signalR.HubConnectionState.Connected) {
      return Promise.resolve(true);
    }

    if (this.reconnectPromise) {
      return this.reconnectPromise;
    }

    this.reconnecting = true;
    this.reconnectPromise = this.connection.start()
      .then(() => {
        return true;
      })
      .catch(error => {
        console.error('SignalR reconnect failed', error);
        this.scheduleReconnect();
        return false;
      })
      .then(connected => {
        this.reconnecting = false;
        this.reconnectPromise = null;
        return connected;
      });

    return this.reconnectPromise;
  }
  
}
